/**
 * AppToaster — mounts react-hot-toast's Toaster once at the app root.
 *
 * - top-right on desktop (>=768px), top-center on mobile (<768px)
 * - WCAG 2.1 AA: aria-live regions configured per toast via ariaProps
 * - Click-to-scroll: reads click handlers from the toast lib registry
 *   (react-hot-toast has no native onClick — handled via children render prop)
 * - Design system: IBM Plex Mono font, dark bg var(--axg-surface-1), green accent var(--axg-accent)
 *
 * THEME-EXCEPTION (WS-A2 / WS-A3 guard allowlist): toasts are intentionally
 * dark-on-purpose in BOTH themes — react-hot-toast renders in a portal at the
 * app root, above any themed surface, so a single high-contrast dark chrome is
 * the deliberate, on-brand choice. The hardcoded literals below (var(--axg-surface-1) / var(--axg-text-primary) /
 * IBM Plex Mono) are therefore exempt from the token-source regression scan and
 * are listed in its documented allowlist. Do not "tokenize" without re-deciding.
 */
import toast, { Toaster, ToastBar } from 'react-hot-toast'
import type { Toast } from 'react-hot-toast'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getClickHandler } from '../lib/toast'

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768
}

export default function AppToaster() {
  const { t: translate } = useTranslation()
  const position = isMobile() ? ('top-center' as const) : ('top-right' as const)

  return (
    <Toaster
      position={position}
      gutter={8}
      containerStyle={{ zIndex: 9999 }}
      toastOptions={{
        style: {
          background: 'var(--axg-surface-1)',
          color: 'var(--axg-text-primary)',
          border: '1px solid var(--axg-border-strong)',
          fontFamily: 'var(--font-mono)',
          fontSize: '13px',
          borderRadius: '8px',
          maxWidth: '380px',
        },
        success: {
          iconTheme: { primary: 'var(--axg-accent)', secondary: 'var(--axg-surface-1)' },
        },
        error: {
          iconTheme: { primary: 'var(--axg-severity-crit)', secondary: 'var(--axg-surface-1)' },
        },
      }}
    >
      {(t: Toast) => {
        const handler = getClickHandler(t.id)
        return (
          <div onClick={handler} style={{ width: '100%' }} role="presentation">
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  {/* Every notice can be closed by hand (QA 02/11/2026: a stuck
                      "Memória atualizada" had no way out). Loading notices
                      belong to work still running, so they keep no close. */}
                  {t.type !== 'loading' && (
                    <button
                      type="button"
                      data-testid="toast-dismiss"
                      aria-label={translate('common.close')}
                      onClick={(event) => {
                        event.stopPropagation()
                        toast.dismiss(t.id)
                      }}
                      className="touch-target inline-flex shrink-0 items-center justify-center rounded-md text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-axg-accent/50"
                    >
                      <X size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                  )}
                </>
              )}
            </ToastBar>
          </div>
        )
      }}
    </Toaster>
  )
}
